import React, { useMemo } from "react";
import "./kpiStrip.css";

export default function KpiStrip({ selectedPeriod = "90D", data, variant }) {
  if (!data) return null;
  
  const metrics = data?.market_analysis?.market_metrics || {};
  const activeListings = data?.active_listings || data?.market_analysis?.active_listings || [];
  const recentSales = data?.market_analysis?.recent_sales || [];

  // Filter data by time period
  const filterByPeriod = useMemo(() => {
    const now = new Date();
    let daysAgo = 30;
    if (selectedPeriod === "60D") daysAgo = 60;
    else if (selectedPeriod === "90D") daysAgo = 90;
    
    const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
    
    return (dateString) => {
      if (!dateString) return false;
      const date = new Date(dateString);
      return date >= cutoffDate;
    };
  }, [selectedPeriod]);

  const RADIUS_MI = 100;

  // Calculate active count - within 100 mile radius (listings without distance_miles are included)
  const activeCount = useMemo(() => {
    return activeListings.filter(listing => {
      const d = listing.distance_miles ?? listing.distance_mi ?? listing.distance;
      return d == null || d <= RADIUS_MI;
    }).length;
  }, [activeListings]);

  const soldCount = useMemo(() => {
    return recentSales
      .filter(sale => filterByPeriod(sale.last_seen_at_date || sale.sold_date)).length;
  }, [recentSales, filterByPeriod]);

  // Period days for MDS and labels (30, 60, or 90)
  const periodDays = useMemo(() => {
    if (selectedPeriod === "30D") return 30;
    if (selectedPeriod === "60D") return 60;
    return 90;
  }, [selectedPeriod]);

  // MDS (Market Days Supply) = Active / (Sold / periodDays) = activeCount * periodDays / soldCount (based on selected period)
  const mds = useMemo(() => {
    if (soldCount <= 0) return null;
    const dailyRate = soldCount / periodDays;
    return Math.round(activeCount / dailyRate);
  }, [activeCount, soldCount, periodDays]);

  // Calculate average Sold DOM from filtered sold listings (based on time period)
  const avgDOM = useMemo(() => {
    const filteredSold = recentSales
      .filter(sale => filterByPeriod(sale.last_seen_at_date || sale.sold_date));
    
    if (filteredSold.length === 0) return null;
    
    const totalDOM = filteredSold.reduce((sum, sale) => sum + (sale.dom_active || sale.dom || 0), 0);
    return Math.round(totalDOM / filteredSold.length);
  }, [recentSales, filterByPeriod]);

  if (variant === "mobile") {
    return (
      <div className="kpi-strip kpi-strip-mobile">
        <div className="kpi-tile kpi-tile-mobile">
          <div className="kpi-label">Active:</div>
          <div className="kpi-value">{activeCount}</div>
        </div>
        <div className="kpi-tile kpi-tile-mobile">
          <div className="kpi-label">Sold:</div>
          <div className="kpi-value">{soldCount}</div>
        </div>
        <div className="kpi-tile kpi-tile-mobile">
          <div className="kpi-label">Avg DOM:</div>
          <div className="kpi-value-group">
            <span className="kpi-value">{avgDOM != null ? avgDOM : "—"}</span>
            {avgDOM != null && <span className="kpi-unit">days</span>}
          </div>
        </div>
        <div className="kpi-tile kpi-tile-mobile">
          <div className="kpi-label">MDS:</div>
          <div className="kpi-value-group">
            <span className="kpi-value">{mds != null ? mds : "—"}</span>
            {mds != null && <span className="kpi-unit">days</span>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="kpi-strip-container">
      <div className="kpi-market-summary">
        <div className="kpi-strip">
          <div className="kpi-tile">
            <div className="kpi-label">Units Active</div>
            <div className="kpi-value">{activeCount}</div>
          </div>
          <div className="kpi-tile">
            <div className="kpi-label">Sold ({selectedPeriod})</div>
            <div className="kpi-value">{soldCount}</div>
          </div>
          <div className="kpi-tile">
            <div className="kpi-label">Avg DOM</div>
            <div className="kpi-value-group">
              <span className="kpi-value">{avgDOM != null ? avgDOM : "—"}</span>
              {avgDOM != null && <span className="kpi-unit">Days</span>}
            </div>
          </div>
          <div className="kpi-tile">
            <div className="kpi-label">MDS ({selectedPeriod})</div>
            <div className="kpi-value-group">
              <span className="kpi-value">{mds != null ? mds : "—"}</span>
              {mds != null && <span className="kpi-unit">Days</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

